'use client';
// ──────────────────────────────────────────────────────
// ChatDrawer — QIO AI Chat Sliding Panel
// Clean white theme with teal accents
// ──────────────────────────────────────────────────────
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import dynamic from 'next/dynamic';
import { OnScreenKeyboard } from './OnScreenKeyboard';
import { CardRenderer } from './chat/CardRenderer';
import { MessageActions, MessageActionsStyles } from './chat/MessageActions';
import type { ProductSuggestion, AssistantBlock, ChatMeta, CreditStatus } from '@/lib/types';
import { ONBOARDING_HINTS, classifyPhoneInput, PHONE_ERROR_COPY, looksLikeOtp, verifyOtpSession } from '@/lib/onboarding';
import { loadTranscript, saveTranscript } from '@/lib/chat-transcript';
// QCARE-CHAT-MARKDOWN-LINKS (2026-05-12) — `stripMarkdown` is the
// clipboard-path normalizer (now collapses `[label](url)` → label);
// `renderAssistantText` is the display-path renderer (returns React
// nodes with safe `<a>` anchors). See lib/assistant-text.tsx.
import { stripMarkdown, renderAssistantText } from '@/lib/assistant-text';
import { makeImageThumbnail } from '@/lib/image-thumbnail';
import { makeMessageId } from '@/lib/message-id';
import { useMessageActions } from '@/lib/useMessageActions';
import {
    collectProductIds,
    mergeProductImage,
    useProductImageHydration,
} from '@/lib/product-image-hydration';
// Same kiosk-identity helper used by scan/heartbeat — sends X-Kiosk-Key
// header + kioskId in the body so the backend KioskKeyGuard accepts the
// SSE request. Without this, /api/backend/ai/chat/v2/stream returns 401
// in any environment that doesn't set DEMO_BYPASS_KIOSK_AUTH=true.
import {
    resolveIdentity,
    buildKioskHeaders,
    clearRuntimeCredentials,
} from '@/lib/kiosk-identity';
// Phase 9R — TrustModal opens directly from authenticity_query chips
// instead of looping the chip text back as a chat message. Without this
// shortcut, tapping "🔒 عرض شهادة الأصالة" would trigger another
// authenticity_query turn that emits the same response again — a
// dead-end loop the user can't escape.
import { TrustModal } from './TrustModal';
import { TRUST_CHIP_LABELS, isTrustChipText } from '@/lib/trust-chips';
import { applyChipPolicy } from '@/lib/chip-policy';

/** Format seconds as HH:MM:SS for the credit-reset countdown. */
function formatCountdown(secs: number): string {
    const s = Math.max(0, Math.floor(secs));
    const hh = Math.floor(s / 3600);
    const mm = Math.floor((s % 3600) / 60);
    const ss = s % 60;
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(hh)}:${pad(mm)}:${pad(ss)}`;
}

// QCARE-CHAT-MARKDOWN-LINKS (2026-05-12): `stripMarkdown` +
// `renderAssistantText` now live in `lib/assistant-text.tsx`
// (imported above). Display sites below use `renderAssistantText`
// to render safe `<a>` anchors for inline Markdown links; the
// clipboard COPY path uses `stripMarkdown` which collapses
// `[label](url)` → label.

interface ChatMessage {
    role: 'user' | 'ai';
    text: string;
    imageUrl?: string;
    // QCARE-IMAGE-UPLOAD-THUMBNAIL-PERSISTENCE-HOTFIX (2026-05-02):
    // Small JPEG data URL (≤160px, q≈0.55, capped at ~24KB) persisted
    // in localStorage so the upload preview survives a refresh.
    // Recognition uses the full-size `imageUrl` on the live request
    // only — the thumbnail is display-only.
    imageThumbUrl?: string;
    suggestions?: string[];
    productSuggestions?: ProductSuggestion[];
    blocks?: AssistantBlock[];
    meta?: ChatMeta;
    // Stable per-bubble id used as the feedback primary key on the backend.
    // Generated client-side when the bubble first enters local state. Never
    // echoed through the chat API; not persisted in the transcript payload.
    id?: string;
}

/**
 * Sentinel thrown by streamChatTokens when the backend rejects the
 * request with 401 Unauthorized. The bootstrap effect uses this to
 * skip the silent-empty-drawer path (which masks a real auth break)
 * and instead trip the same DeviceGate-revoked flow that scan and
 * heartbeat use — so the user is never staring at a blank drawer
 * while the device is silently unauthenticated.
 */
export const CHAT_STREAM_UNAUTHORIZED = 'CHAT_STREAM_UNAUTHORIZED';

/** Stream SSE tokens from /ai/chat/v2/stream */
async function* streamChatTokens(message: string, history?: { role: string, content: string }[], sessionId?: string | null): AsyncGenerator<{ token?: string; done?: boolean; suggestions_ar?: string[]; productSuggestions?: ProductSuggestion[]; blocks?: AssistantBlock[]; refusal?: boolean; meta?: ChatMeta; creditStatus?: CreditStatus | null; onboardingActive?: boolean; onboardingStep?: string; customerId?: string }> {
    // Resolve kiosk identity from localStorage (runtime-provisioned) or
    // env (NEXT_PUBLIC_KIOSK_*). The same helper backs scan/heartbeat;
    // unifying the auth contract is the entire point of this fix.
    const identity = resolveIdentity();
    const res = await fetch(`/api/backend/ai/chat/v2/stream`, {
        method: 'POST',
        // buildKioskHeaders sets Content-Type + X-Kiosk-Key (and
        // x-kiosk-runtime when the credentials came from localStorage,
        // so the proxy knows not to override them with env values).
        headers: buildKioskHeaders(identity),
        // Same-origin, but declare explicitly so the `qio_session` cookie
        // reaches the backend through the /api/backend catch-all proxy and
        // MobileSessionMiddleware can hydrate the session (skipping the
        // inline phone/OTP flow for already-authenticated visitors).
        credentials: 'include',
        // kioskId is required by KioskKeyGuard — it pairs with the bcrypt
        // hash stored on the kiosk row to authenticate the device. Without
        // it the guard short-circuits with `Missing X-Kiosk-Key or kioskId`.
        body: JSON.stringify({
            message,
            history: history || [],
            sessionId,
            kioskId: identity.kioskId,
        }),
    });
    // 401 = stale or missing kiosk creds. Mirror lib/api.ts:114-125 (scan
    // path) so a runtime-provisioned device that lost its credentials
    // self-heals by clearing localStorage + dispatching the auth-revoked
    // event the parent page listens to (app/page.tsx:62-68 → DeviceGate).
    // We always throw a tagged sentinel so the bootstrap effect can
    // distinguish auth failure from generic stream errors.
    if (res.status === 401) {
        if (identity.source === 'localStorage') {
            clearRuntimeCredentials();
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('kiosk-auth-revoked'));
            }
        }
        throw new Error(CHAT_STREAM_UNAUTHORIZED);
    }
    if (!res.ok || !res.body) throw new Error('Stream request failed');

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Parse SSE lines
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // keep incomplete line

        for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            const json = line.slice(6).trim();
            if (!json) continue;
            try {
                const event = JSON.parse(json);
                if (event.done) {
                    yield {
                        done: true,
                        suggestions_ar: event.suggestions_ar,
                        productSuggestions: event.productSuggestions,
                        blocks: event.blocks,
                        refusal: event.refusal,
                        meta: event.meta,
                        creditStatus: event.creditStatus ?? null,
                        onboardingActive: typeof event.onboarding_active === 'boolean' ? event.onboarding_active : undefined,
                        // Surfaced by the backend on onboarding prompts (currently
                        // only 'awaiting_otp'). The ChatDrawer uses it to route
                        // the next OTP-shaped input to /api/mobile/auth/verify-otp
                        // rather than stuffing it back through the chat stream —
                        // same behavior as the mobile /m/q page.
                        onboardingStep: typeof event.onboarding_step === 'string' ? event.onboarding_step : undefined,
                        // Surfaced so desktop consumers can persist qcare_customer_id
                        // from any authenticated done frame — not only from
                        // verify-otp. Essential for returning cookie-authed users
                        // whose JWT is still valid on refresh and never re-enter
                        // the OTP flow; without this their browser has no eligible
                        // id to key transcript persistence on.
                        customerId: typeof event.customerId === 'string' && event.customerId.length > 0
                            ? event.customerId
                            : undefined,
                    };
                } else if (event.t) {
                    yield { token: event.t };
                }
            } catch { /* skip malformed */ }
        }
    }
}

function ChatDrawerInner({ open, onClose }: { open: boolean; onClose: () => void }) {
    // Identity for transcript persistence. Populated from the same key the
    // mobile /m/q page uses — written here via verify-otp success or (as a
    // fallback) on the SSE terminal onboarding welcome, and consumed by the
    // load/save helpers below. Kept null for anonymous visitors so their
    // turns are never persisted (prevents anon-era content leaking once
    // they authenticate).
    const [customerId, setCustomerId] = useState<string | null>(() => {
        if (typeof window === 'undefined') return null;
        const v = window.localStorage.getItem('qcare_customer_id');
        return (v && !v.startsWith('anon_')) ? v : null;
    });

    // Transcript is the source of truth for the UI. `null` explicitly means
    // "restore has not finished yet" — distinct from "restore finished and
    // there was nothing to load" (= `[]`). This tri-state lets the bootstrap
    // gate refuse to run before the transcript is known, so the backend
    // welcome is never able to paint over a prior conversation.
    const [messages, setMessages] = useState<ChatMessage[] | null>(null);
    const [isRestored, setIsRestored] = useState(false);
    // Phase 9R — TrustModal open state. Triggered by tapping a trust
    // chip ("🔒 عرض شهادة الأصالة", "عرض شهادات الاعتماد", ...) on an
    // authenticity_query response. Without this, the chip text would
    // round-trip as a new chat message and re-trigger the same answer.
    const [trustModalOpen, setTrustModalOpen] = useState(false);
    const restoreRanRef = useRef(false);
    const [input, setInput] = useState('');
    const [imageBase64, setImageBase64] = useState<string | null>(null);
    const [isTyping, setIsTyping] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [showOSK, setShowOSK] = useState(false);
    const [oskHeight, setOskHeight] = useState(0);
    // Persistent kiosk session id — continuity across page refresh. Keyed
    // in localStorage so a reload reuses the same Redis-side session state
    // instead of minting a new "first-visit" identity every time. JWT
    // cookie remains the real per-user identity gate; this id is just the
    // Redis key for the chat session blob. Client-only because dynamic
    // ({ ssr: false }) wraps this component; the typeof window guard is
    // defensive for SSR-safe code movement.
    const [sessionId, setSessionId] = useState<string | null>(() => {
        if (typeof window === 'undefined') return null;
        let sid = window.localStorage.getItem('qcare_session_id');
        if (!sid) {
            sid = 'kiosk-' + crypto.randomUUID();
            window.localStorage.setItem('qcare_session_id', sid);
        }
        return sid;
    });
    // setSessionId is exposed for future identity-reset flows (e.g. admin
    // "new session" action) — intentionally unused in the normal lifecycle.
    void setSessionId;
    // Credit indicator state — null until the backend returns a creditStatus
    // (anonymous sessions or before the first done event). Updated on every
    // SSE done frame so it always reflects the post-decrement snapshot.
    const [creditStatus, setCreditStatus] = useState<CreditStatus | null>(null);
    // Local clock for the reset-countdown. Derived live from creditStatus.nextResetAt
    // rather than mutating secondsRemaining — so a stale backend snapshot
    // still ticks correctly against the authoritative nextResetAt anchor.
    const [nowMs, setNowMs] = useState(() => Date.now());
    const scrollRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // ── Onboarding first-touch state ───────────────────
    // hintsActive gates the rotating hint (below the first AI bubble);
    // flips to false on focus/typing/send. hasSentFirstMessage gates the
    // one-shot local phone pre-validation.
    //
    // hintsArmedRef guards the focus/typing stop triggers until the drawer
    // auto-focus sequence has settled. forceFocus fires at T+350ms and
    // T+700ms (plus rAF + 150ms inner retries) and every one of those
    // programmatic focus events is dispatched by the browser's focus
    // manager with isTrusted=true — so the prior isTrusted-gate no-op'd.
    // This ref is armed 1000 ms after drawer open, past every focus pass.
    // handleSend remains unarmed because a send is always a real user action.
    const [hintsActive, setHintsActive] = useState(true);
    const [hintIndex, setHintIndex] = useState(0);
    const [hasSentFirstMessage, setHasSentFirstMessage] = useState(false);
    const hintsArmedRef = useRef(false);

    // ── Auth routing state (parity with mobile /m/q) ──
    // `awaitingOtp` is flipped true when the most recent backend SSE done
    // frame carried onboarding_step='awaiting_otp'. While true, the next
    // OTP-shaped input is handed to /api/mobile/auth/verify-otp instead
    // of the chat stream — so successful verification sets the
    // qio_session cookie and refresh no longer kicks the user back to
    // phone/OTP. Falls back to the chat OTP path on a non-ok response
    // so the inline error copy (wrong code / expired / max attempts)
    // still flows through.
    const [awaitingOtp, setAwaitingOtp] = useState(false);
    const lastPhoneRef = useRef<string>('');

    // Shared message-action state + handlers. The hook owns the per-
    // customer reaction map, the overflow-menu index, the document-
    // level outside-click / Escape dismiss, and /api/mobile/chat/feedback
    // GET + POST. `isReady` gates hydration on transcript restore so
    // the GET never races an empty bubble list.
    const messageActions = useMessageActions({
        customerId,
        sessionId,
        isReady: isRestored,
    });

    // ── Transcript restore phase (side-drawer lifecycle) ──
    // Runs the FIRST time the drawer opens and never again for the life of
    // the component. Reads localStorage on the client (dynamic({ ssr:false })
    // guarantees we're on the client at this point), maps the persisted
    // shape into ChatMessage, and flips `isRestored` so the bootstrap
    // welcome effect below has a strong happens-before gate to wait on.
    //
    // Ref guard is explicit so React 19 StrictMode's dev double-invoke of
    // effects can't run the body twice; any re-run after a subsequent
    // `open` flip or `customerId` upgrade is a no-op.
    useEffect(() => {
        if (!open) return;
        if (restoreRanRef.current) return;
        restoreRanRef.current = true;
        // Transcript defines the UI. Always move `messages` from the null
        // sentinel into a concrete array here — either the restored turns
        // or an explicit empty array — so the bootstrap gate below sees
        // a deterministic post-restore state.
        const restored = loadTranscript(customerId);
        if (restored.length > 0) {
            setMessages(restored.map(t => ({
                role: t.role,
                text: t.text,
                suggestions: t.chips,
                imageUrl: t.imageUrl,
                imageThumbUrl: t.imageThumbUrl,
                // Preserve the persisted stable id so restored AI bubbles
                // can still be clicked and their feedback routed to the
                // correct backend row. Legacy transcripts saved before the
                // id field was persisted return `undefined` here — mint a
                // fresh id in that case so the reaction handler's
                // `if (!messageId) return;` guard and the render's
                // `msg.id && messageReactions[msg.id]` gate both see a
                // truthy id. The next saveTranscript commit persists this
                // minted id, so future refreshes reuse it.
                id: t.id ?? makeMessageId(),
                // Card-payload passthrough — undefined for legacy transcripts
                // persisted before this field set, which is intentional: the
                // render cascade falls back to text-only bubbles for those
                // (current behavior). When present, the modern blocks branch
                // repaints product_title / narrative / product_card / bridge
                // exactly like pre-refresh.
                blocks: t.blocks,
                productSuggestions: t.productSuggestions,
                meta: t.meta,
            })));
        } else {
            setMessages([]);
        }
        setIsRestored(true);
    }, [open, customerId]);

    useEffect(() => {
        if (!open || !hintsActive) return;
        const id = setInterval(() => {
            setHintIndex(i => (i + 1) % ONBOARDING_HINTS.length);
        }, 2800);
        return () => clearInterval(id);
    }, [open, hintsActive]);

    // Arm stop-on-focus / stop-on-typing after the drawer-open auto-focus
    // sequence settles. Re-armed on every open-close cycle.
    useEffect(() => {
        if (!open) { hintsArmedRef.current = false; return; }
        const t = setTimeout(() => { hintsArmedRef.current = true; }, 1000);
        return () => clearTimeout(t);
    }, [open]);

    useEffect(() => {
        if (!open) return;
        if (!creditStatus?.nextResetAt) return;
        const id = setInterval(() => setNowMs(Date.now()), 1000);
        return () => clearInterval(id);
    }, [open, creditStatus?.nextResetAt]);

    const displaySecondsRemaining = creditStatus?.nextResetAt
        ? Math.max(0, Math.round((new Date(creditStatus.nextResetAt).getTime() - nowMs) / 1000))
        : (creditStatus?.secondsRemaining ?? 0);

    // First-open mount fetch — asks the backend for the initial message so
    // the onboarding gate (for fresh sessions) or the empty-message greeting
    // (for onboarded sessions) is always the first bubble the user sees.
    //
    // Side-drawer lifecycle contract (restore-then-bootstrap):
    //   1. open must be true                         — the drawer is visible
    //   2. isRestored must be true                   — transcript restore ran
    //   3. mountRequestSentRef must not be already used
    //   4. messages.length === 0                     — no restored bubbles
    //
    // Gating on `isRestored` is what prevents the bootstrap from racing
    // ahead of the restore effect: if a transcript exists on disk, the
    // restore effect calls setMessages(restored) + setIsRestored(true) in
    // the same render, so by the time this effect sees isRestored=true,
    // `messages.length > 0` and the bootstrap is (correctly) skipped.
    //
    // Ref guard is StrictMode-safe: React double-invokes effects in dev, but
    // the second run short-circuits. Cleared in the error path so the next
    // user submit naturally re-enters the backend flow.
    const mountRequestSentRef = useRef(false);
    useEffect(() => {
        // Transcript-driven lifecycle guards. The bootstrap welcome is only
        // allowed when:
        //   - the drawer is actually open
        //   - the restore phase has completed
        //   - the transcript is KNOWN (not still in the `null` sentinel)
        //   - the known transcript is EMPTY (nothing to continue from)
        // Any other state means either the user already has a conversation
        // to continue or we don't yet know — in both cases bootstrap would
        // overwrite real history.
        if (!open) return;
        if (!isRestored) return;
        if (messages === null) return;
        if (messages.length > 0) return;
        if (mountRequestSentRef.current) return;
        mountRequestSentRef.current = true;
        void (async () => {
            setIsTyping(true);
            let streamText = '';
            let gotFirstToken = false;
            let finalSuggestions: string[] | undefined;
            let finalProductSuggestions: ProductSuggestion[] | undefined;
            let finalBlocks: AssistantBlock[] | undefined;
            let finalMeta: ChatMeta | undefined;
            let finalStep: string | undefined;
            let finalOnboardingActive: boolean | undefined;
            try {
                for await (const event of streamChatTokens('', [], sessionId)) {
                    if (event.token) {
                        streamText += event.token;
                        if (!gotFirstToken) {
                            gotFirstToken = true;
                            setIsTyping(false);
                            setMessages(prev => [...(prev ?? []), { role: 'ai', text: streamText, id: makeMessageId() }]);
                        } else {
                            const currentText = streamText;
                            setMessages(prev => {
                                if (!prev || prev.length === 0) return prev;
                                const updated = [...prev];
                                updated[updated.length - 1] = { ...updated[updated.length - 1], text: currentText };
                                return updated;
                            });
                        }
                    } else if (event.done) {
                        finalSuggestions = event.suggestions_ar;
                        finalProductSuggestions = event.productSuggestions;
                        finalBlocks = event.blocks;
                        finalMeta = event.meta;
                        finalStep = event.onboardingStep;
                        finalOnboardingActive = event.onboardingActive;
                        if (event.creditStatus) setCreditStatus(event.creditStatus);
                        // Capture customerId from any authenticated done frame
                        // (empty-message welcome, normal chat) and mirror it to
                        // localStorage so transcript persistence has a real
                        // eligible id to key on — mirrors the /m/q:212 pattern.
                        if (event.customerId && event.customerId !== customerId) {
                            try { window.localStorage.setItem('qcare_customer_id', event.customerId); } catch { /* storage disabled */ }
                            setCustomerId(event.customerId);
                        }
                    }
                }
                // Mirror /m/q: track whether the backend is currently waiting
                // for an OTP so the next user input routes to the auth endpoint.
                if (finalStep === 'awaiting_otp') {
                    setAwaitingOtp(true);
                } else if (finalOnboardingActive !== true || (finalStep && finalStep !== 'awaiting_otp')) {
                    setAwaitingOtp(false);
                }
                if (gotFirstToken) {
                    let cleanText = streamText;
                    const chipsIdx = cleanText.indexOf('[CHIPS]');
                    if (chipsIdx !== -1) cleanText = cleanText.substring(0, chipsIdx).trimEnd();
                    setMessages(prev => {
                        if (!prev || prev.length === 0) return prev;
                        const updated = [...prev];
                        updated[updated.length - 1] = {
                            ...updated[updated.length - 1],
                            text: cleanText,
                            suggestions: finalSuggestions,
                            productSuggestions: finalProductSuggestions,
                            blocks: finalBlocks,
                            meta: finalMeta,
                        };
                        return updated;
                    });
                }
            } catch (err) {
                // 401 → kiosk creds missing/stale. The fetch helper already
                // dispatched 'kiosk-auth-revoked' for runtime-provisioned
                // devices; the parent page (app/page.tsx) listens for that
                // event and shows DeviceGate. Either way we surface a single
                // line of copy in the drawer instead of leaving it blank,
                // because a silent empty drawer reads as "the model died".
                if (err instanceof Error && err.message === CHAT_STREAM_UNAUTHORIZED) {
                    setMessages(prev => [...(prev ?? []), {
                        role: 'ai',
                        text: 'تعذّر الاتصال بالخدمة — يرجى إعادة المحاولة أو تنبيه الفريق.',
                        id: makeMessageId(),
                    }]);
                } else {
                    // Other failures (network, malformed SSE, etc.) keep the
                    // existing silent-fail behavior — clear the guard so the
                    // next user submit naturally re-enters the backend flow.
                    mountRequestSentRef.current = false;
                }
            } finally {
                setIsTyping(false);
            }
        })();
        // `messages` is intentionally omitted — the ref guard already makes
        // this effect fire once per drawer lifetime, and we only read the
        // post-restore value to decide whether to skip the bootstrap.
        // Re-running on every message change would defeat the guard and
        // re-fire the empty-turn mid-conversation. `isRestored` IS in the
        // deps so the effect wakes up exactly once after the restore phase
        // completes — that's the happens-before edge that prevents the
        // race the prior design had.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, sessionId, isRestored]);

    // Measured keyboard height callback
    const handleOSKHeight = useCallback((h: number) => setOskHeight(h), []);

    // Touch detection (client-side only)
    const isTouchCapable = typeof window !== 'undefined' &&
        ('ontouchstart' in window || navigator.maxTouchPoints > 0);

    // Auto-scroll to bottom
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, isTyping]);

    // Persist the last ~10 bubbles per real customer so refreshing the page
    // restores the conversation instead of blanking it. Anonymous visitors
    // (customerId null) are skipped inside saveTranscript so pre-login turns
    // don't leak once they authenticate. Desktop stores `chips` as
    // `suggestions` locally — mapped here so the on-disk contract matches
    // the mobile /m/q surface exactly. Heavy fields (product cards, block
    // trees, base64 images) are dropped at the call site + inside the
    // helper for a compact, repaintable shape.
    //
    // Gated on `isRestored` so we never write back BEFORE the restore
    // phase has read the prior transcript. Without this gate, the very
    // first post-mount commit (messages=[], customerId=real_id restored
    // from localStorage in useState init) would fire saveTranscript with
    // an empty array and overwrite the stored transcript before the
    // open-gated restore effect had a chance to load it — which was the
    // observed data-loss bug on refresh.
    useEffect(() => {
        if (!isRestored) return;
        if (messages === null) return;
        saveTranscript(customerId, messages.map(m => ({
            role: m.role,
            text: m.text,
            chips: m.suggestions,
            imageUrl: m.imageUrl,
            // QCARE-IMAGE-UPLOAD-THUMBNAIL-PERSISTENCE-HOTFIX (2026-05-02):
            // saveTranscript persists the small JPEG thumb so the upload
            // preview survives refresh; full-size data: imageUrl stays
            // stripped (quota).
            imageThumbUrl: m.imageThumbUrl,
            id: m.id,
            // Card-payload passthrough — the save helper only writes these
            // when non-empty (see chat-transcript.ts), so user messages
            // (no blocks / suggestions / meta) stay compact on disk.
            blocks: m.blocks,
            productSuggestions: m.productSuggestions,
            meta: m.meta,
        })));
    }, [customerId, messages, isRestored]);

    // ── forceFocus: maximizes touch keyboard open success ──
    const forceFocus = useCallback(() => {
        const el = inputRef.current;
        if (!el) return;
        // Ensure not readonly
        el.removeAttribute('readonly');
        // Primary focus
        el.focus({ preventScroll: true });
        // Some mobile browsers need click() to trigger keyboard
        el.click();
        // setSelectionRange can trigger keyboard in WebKit
        const v = el.value ?? '';
        try { el.setSelectionRange(v.length, v.length); } catch { /* non-text input */ }
        // Retry with rAF + short delay for stubborn devices
        requestAnimationFrame(() => el.focus({ preventScroll: true }));
        setTimeout(() => el.focus({ preventScroll: true }), 150);
        console.debug('[ChatDrawer] forceFocus, active:', document.activeElement === el);
    }, []);

    const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const dataUrl = event.target?.result as string;
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const MAX_WIDTH = 800;
                const MAX_HEIGHT = 800;
                let width = img.width;
                let height = img.height;

                if (width > height) {
                    if (width > MAX_WIDTH) { height *= MAX_WIDTH / width; width = MAX_WIDTH; }
                } else {
                    if (height > MAX_HEIGHT) { width *= MAX_HEIGHT / height; height = MAX_HEIGHT; }
                }
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx?.drawImage(img, 0, 0, width, height);
                setImageBase64(canvas.toDataURL('image/jpeg', 0.8));
            };
            img.src = dataUrl;
        };
        reader.readAsDataURL(file);
        // Clear input value so same file can be selected again if canceled
        e.target.value = '';
    };

    // Focus input + auto-show keyboard after drawer animation
    useEffect(() => {
        if (!open) { setShowOSK(false); return; }
        const t1 = setTimeout(forceFocus, 350);
        const t2 = setTimeout(forceFocus, 700);
        // Auto-show in-app keyboard on touch devices
        const t3 = isTouchCapable ? setTimeout(() => setShowOSK(true), 400) : undefined;
        return () => { clearTimeout(t1); clearTimeout(t2); if (t3) clearTimeout(t3); };
    }, [open, forceFocus, isTouchCapable]);

    const handleSend = useCallback(async (text?: string) => {
        const msg = (text || input).trim() || (imageBase64 ? 'إليك هذه الصورة' : '');
        if (!msg || msg.length > 500 || isTyping) return;

        // Stop the rotating hint on first real interaction.
        setHintsActive(false);

        // Any valid phone-shaped input updates lastPhoneRef so a subsequent
        // OTP send can reach /api/mobile/auth/verify-otp with the right phone.
        // Runs on every send (not just the first) so a retry after a phone
        // error still refreshes the captured value. Mirrors /m/q:320.
        if (!imageBase64 && classifyPhoneInput(msg) === 'ok') {
            lastPhoneRef.current = msg;
        }

        // One-shot local phone pre-validation on the user's first send.
        // Catches short / long / wrong-prefix shapes with precise copy
        // before involving the backend. Valid shapes fall through.
        if (!imageBase64 && !hasSentFirstMessage) {
            const phoneClass = classifyPhoneInput(msg);
            if (phoneClass === 'short' || phoneClass === 'long' || phoneClass === 'prefix') {
                setInput('');
                setMessages(prev => [
                    ...(prev ?? []),
                    { role: 'user', text: msg },
                    { role: 'ai', text: PHONE_ERROR_COPY[phoneClass], id: makeMessageId() },
                ]);
                return;
            }
        }

        const currentImage = imageBase64;
        // QCARE-IMAGE-UPLOAD-THUMBNAIL-PERSISTENCE-HOTFIX (2026-05-02):
        // Mint a stable id so the async thumbnail (resolved below) can
        // patch the same bubble after the canvas resize completes.
        const userMsgId = makeMessageId();
        setMessages(prev => [...(prev ?? []), { role: 'user', text: msg, imageUrl: currentImage || undefined, id: userMsgId }]);
        setInput('');
        setImageBase64(null);
        setHasSentFirstMessage(true);
        setIsTyping(true);

        // Generate a tiny JPEG thumbnail off the original upload and patch
        // it onto the just-appended user message. saveTranscript persists
        // imageThumbUrl across refresh so the bubble doesn't appear blank.
        // Recognition uses the full-size `currentImage` on the live request
        // to /api/mobile/chat — the thumbnail is display-only.
        if (currentImage) {
            void makeImageThumbnail(currentImage).then(thumb => {
                if (!thumb) return;
                setMessages(prev => prev?.map(m =>
                    m.id === userMsgId ? { ...m, imageThumbUrl: thumb } : m
                ) ?? prev);
            });
        }

        // ── Image-message branch (desktop ↔ mobile parity) ──
        // QCARE-VISION-MODEL-FLIP-AND-DESKTOP-MOBILE-PARITY (2026-05-02):
        // image messages bypass the SSE pipeline because vision recognition
        // is non-streaming (the vision LLM blocks until JSON parse). Route
        // through /api/mobile/chat — the same path /m/q uses today — so
        // MobileImageService runs once and the catalog-grounded reply
        // renders into the same chat bubble UI as text messages. No
        // separate desktop matching logic; no SSE extension. customerId is
        // forwarded if known; otherwise a kiosk-session sentinel is used,
        // which the backend treats as an anonymous mobile session.
        if (currentImage) {
            try {
                const sessionLikeId = customerId || `kiosk-${sessionId || 'anon'}`;
                const res = await fetch('/api/mobile/chat', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({
                        customerId: sessionLikeId,
                        message: msg,
                        imageBase64: currentImage,
                        history: [],
                    }),
                });
                const data: { reply?: string; products?: ProductSuggestion[] } | null =
                    res.ok ? await res.json() : null;
                const reply = data?.reply
                    || 'ما قدرت أتعرف على المنتج 😔\nجربي تصوير الباركود أو اكتبي اسم المنتج 🙏';
                setMessages(prev => [
                    ...(prev ?? []),
                    {
                        role: 'ai',
                        text: reply,
                        productSuggestions: Array.isArray(data?.products) && data.products.length > 0
                            ? data.products
                            : undefined,
                        id: makeMessageId(),
                    },
                ]);
            } catch {
                setMessages(prev => [
                    ...(prev ?? []),
                    { role: 'ai', text: 'صار خطأ في معالجة الصورة. جربي مرة ثانية 🙏', id: makeMessageId() },
                ]);
            } finally {
                setIsTyping(false);
            }
            return;
        }

        // ── OTP interception (parity with mobile /m/q) ──
        // If the backend's last done frame carried onboarding_step='awaiting_otp'
        // and the user input is a code-shaped string, verify via the dedicated
        // auth endpoint so the qio_session cookie is issued. On success, fire
        // an empty chat turn to pull the post-login welcome from the
        // authenticated path (the cookie is now in the browser jar and will
        // ride along automatically). On non-ok, fall through to the chat OTP
        // path so inline error copy (wrong-code / expired / max-attempts)
        // still works.
        if (!currentImage && awaitingOtp && looksLikeOtp(msg) && lastPhoneRef.current) {
            const result = await verifyOtpSession(lastPhoneRef.current, msg);
            if (result.ok) {
                setAwaitingOtp(false);
                // Capture the real customerId so transcript persistence can
                // key on it. Same field + storage key the mobile /m/q page
                // uses — desktop joins the shared identity model without
                // altering the kiosk sessionId lifecycle.
                if (result.customerId && typeof window !== 'undefined') {
                    try { window.localStorage.setItem('qcare_customer_id', result.customerId); } catch { /* storage disabled */ }
                    setCustomerId(result.customerId);
                }
                // Fire a blank SSE turn so the server emits the welcome /
                // next-step copy via the authenticated path. We inline a
                // minimal consumer (same shape as the mount effect + the
                // main send loop) so this resume never appends a synthetic
                // user bubble or duplicates the real OTP bubble above.
                try {
                    let resumeText = '';
                    let gotFirst = false;
                    let rSug: string[] | undefined;
                    let rPs: ProductSuggestion[] | undefined;
                    let rBlocks: AssistantBlock[] | undefined;
                    let rMeta: ChatMeta | undefined;
                    let rStep: string | undefined;
                    let rActive: boolean | undefined;
                    for await (const event of streamChatTokens('', [], sessionId)) {
                        if (event.token) {
                            resumeText += event.token;
                            if (!gotFirst) {
                                gotFirst = true;
                                setIsTyping(false);
                                setMessages(prev => [...(prev ?? []), { role: 'ai', text: resumeText, id: makeMessageId() }]);
                            } else {
                                const currentText = resumeText;
                                setMessages(prev => {
                                    if (!prev || prev.length === 0) return prev;
                                    const updated = [...prev];
                                    updated[updated.length - 1] = { ...updated[updated.length - 1], text: currentText };
                                    return updated;
                                });
                            }
                        } else if (event.done) {
                            rSug = event.suggestions_ar;
                            rPs = event.productSuggestions;
                            rBlocks = event.blocks;
                            rMeta = event.meta;
                            rStep = event.onboardingStep;
                            rActive = event.onboardingActive;
                            if (event.creditStatus) setCreditStatus(event.creditStatus);
                        }
                    }
                    if (gotFirst) {
                        let cleanText = resumeText;
                        const idx = cleanText.indexOf('[CHIPS]');
                        if (idx !== -1) cleanText = cleanText.substring(0, idx).trimEnd();
                        setMessages(prev => {
                            if (!prev || prev.length === 0) return prev;
                            const updated = [...prev];
                            updated[updated.length - 1] = {
                                ...updated[updated.length - 1],
                                text: cleanText,
                                suggestions: rSug,
                                productSuggestions: rPs,
                                blocks: rBlocks,
                                meta: rMeta,
                            };
                            return updated;
                        });
                    }
                    if (rStep === 'awaiting_otp') {
                        setAwaitingOtp(true);
                    } else if (rActive !== true || (rStep && rStep !== 'awaiting_otp')) {
                        setAwaitingOtp(false);
                    }
                } catch {
                    // Silent: cookie is set either way; if the resume stream
                    // fails, the next user send will pick up the authenticated
                    // path naturally.
                } finally {
                    setIsTyping(false);
                }
                return;
            }
            // Non-ok: fall through to the normal chat stream so the inline
            // OTP flow surfaces its error copy.
        }

        try {
            // Build history from current messages (last 10, exclude initial greeting).
            // `messages` can still be null here in the theoretical case where the
            // user triggers a send before restore completes; coerce to [] so the
            // request shape stays valid.
            const chatHistory = (messages ?? [])
                .slice(1) // skip initial AI greeting
                .slice(-10) // last 10 messages
                .map(m => ({ role: m.role === 'user' ? 'user' : 'assistant', content: m.text }));

            // Try SSE streaming first
            let streamText = '';
            let gotFirstToken = false;
            let finalSuggestions: string[] | undefined;
            let finalProductSuggestions: ProductSuggestion[] | undefined;
            let finalBlocks: AssistantBlock[] | undefined;
            let finalMeta: ChatMeta | undefined;
            let finalStep: string | undefined;
            let finalOnboardingActive: boolean | undefined;

            try {
                for await (const event of streamChatTokens(msg, chatHistory, sessionId)) {
                    if (event.token) {
                        streamText += event.token;
                        if (!gotFirstToken) {
                            // First token: add AI message and stop typing indicator
                            gotFirstToken = true;
                            setIsTyping(false);
                            setMessages(prev => [...(prev ?? []), { role: 'ai', text: streamText, id: makeMessageId() }]);
                        } else {
                            // Subsequent tokens: update last message
                            const currentText = streamText;
                            setMessages(prev => {
                                if (!prev || prev.length === 0) return prev;
                                const updated = [...prev];
                                updated[updated.length - 1] = { ...updated[updated.length - 1], text: currentText };
                                return updated;
                            });
                        }
                    } else if (event.done) {
                        finalSuggestions = event.suggestions_ar;
                        finalProductSuggestions = event.productSuggestions;
                        finalBlocks = event.blocks;
                        finalMeta = event.meta;
                        finalStep = event.onboardingStep;
                        finalOnboardingActive = event.onboardingActive;
                        if (event.creditStatus) setCreditStatus(event.creditStatus);
                        // Capture customerId from any authenticated done frame
                        // (normal chat) and mirror it to localStorage so
                        // transcript persistence has a real eligible id to
                        // key on — mirrors the /m/q:212 pattern.
                        if (event.customerId && event.customerId !== customerId) {
                            try { window.localStorage.setItem('qcare_customer_id', event.customerId); } catch { /* storage disabled */ }
                            setCustomerId(event.customerId);
                        }
                    }
                }
                // Sync awaitingOtp from the backend's latest state so the
                // next user input routes correctly. Mirrors /m/q:216-223.
                if (finalStep === 'awaiting_otp') {
                    setAwaitingOtp(true);
                } else if (finalOnboardingActive !== true || (finalStep && finalStep !== 'awaiting_otp')) {
                    setAwaitingOtp(false);
                }
            } catch {
                // SSE failed — show error to user
                if (!gotFirstToken) {
                    setMessages(prev => [...(prev ?? []), {
                        role: 'ai',
                        text: 'عذراً، صار خطأ في الاتصال. جربي مرة ثانية 🙏',
                        suggestions: ['أبغى ترشيح لبشرتي', 'إيش المنتجات المتوفرة؟'],
                        id: makeMessageId(),
                    }]);
                    setIsTyping(false);
                    return;
                }
            }

            // Update final message with suggestions + JSON safety parse
            if (gotFirstToken) {
                // Safety: if streamed text is actually JSON, extract reply_ar
                let cleanText = streamText;
                // Strip [CHIPS] marker and everything after it — LLM prompt artifact, not customer-facing
                const chipsMarkerIdx = cleanText.indexOf('[CHIPS]');
                if (chipsMarkerIdx !== -1) cleanText = cleanText.substring(0, chipsMarkerIdx).trimEnd();
                let parsedSuggestions = finalSuggestions;
                const parsedProductSuggestions = finalProductSuggestions;
                const trimmed = streamText.trim();
                if (trimmed.startsWith('{') && trimmed.includes('reply_ar')) {
                    try {
                        const parsed = JSON.parse(trimmed);
                        if (parsed.reply_ar) cleanText = parsed.reply_ar;
                        if (parsed.suggestions_ar && !parsedSuggestions) parsedSuggestions = parsed.suggestions_ar;
                    } catch { /* not valid JSON, use as-is */ }
                }

                setMessages(prev => {
                    if (!prev || prev.length === 0) return prev;
                    const updated = [...prev];
                    updated[updated.length - 1] = {
                        ...updated[updated.length - 1],
                        text: cleanText,
                        suggestions: parsedSuggestions || updated[updated.length - 1].suggestions,
                        productSuggestions: parsedProductSuggestions,
                        blocks: finalBlocks,
                        meta: finalMeta,
                    };
                    return updated;
                });
            }
        } catch {
            setMessages(prev => [...(prev ?? []), { role: 'ai', text: 'عذراً، حدث خطأ. يرجى المحاولة مرة أخرى.', id: makeMessageId() }]);
        } finally {
            setIsTyping(false);
        }
    }, [input, imageBase64, isTyping, messages, sessionId, hasSentFirstMessage, awaitingOtp, customerId]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        handleSend();
    };

    // ── AI message action-bar handlers ──────────────────
    // UI-only: no backend, no telemetry, no network. Reactions live in
    // local state; memory buttons log for now (backend wiring later).

    // ── Image hydration ─────────────────────────────────
    // Collect productIds from every AI message (current + restored from
    // transcript), batch-fetch the latest `imageUrl` once per id-set
    // change, and override only that field at render time. Stored
    // message objects are NOT mutated — see `mergeProductImage` at the
    // CardRenderer call sites below.
    const hydratableProducts = useMemo<ProductSuggestion[]>(() => {
        const acc: ProductSuggestion[] = [];
        for (const m of messages ?? []) {
            if (m.role !== 'ai') continue;
            if (m.productSuggestions && m.productSuggestions.length > 0) acc.push(...m.productSuggestions);
            if (m.blocks && m.blocks.length > 0) {
                for (const b of m.blocks) {
                    if (b.type === 'product_card' && b.product) acc.push(b.product);
                }
            }
        }
        return acc;
    }, [messages]);
    const hydratableIds = useMemo(() => collectProductIds(hydratableProducts), [hydratableProducts]);
    const latestImageMap = useProductImageHydration(hydratableIds);

    return (
        <>
            {/* Onboarding hint keyframe — full-cycle fade-in + hold + fade-out
                over the 2800 ms rotation interval. `forwards` holds the end
                frame so drift between CSS animation end and the React interval
                tick never flashes the previous hint back to full opacity. */}
            <style>{`
                @keyframes q-hint-fade {
                    0%   { opacity: 0; }
                    10%  { opacity: 1; }
                    88%  { opacity: 1; }
                    100% { opacity: 0; }
                }
            `}</style>
            {/* Shared action-row CSS (.qio-action-*) — emitted once, used
                by every AI bubble's MessageActions and by the document
                outside-click listener inside useMessageActions. */}
            <MessageActionsStyles />

            {/* Phase 9R — TrustModal opens here when the user taps a
                trust chip. The chip handler in `msg.suggestions.map`
                routes "🔒 عرض شهادة الأصالة" to setTrustModalOpen(true). */}
            {trustModalOpen && <TrustModal onClose={() => setTrustModalOpen(false)} />}

            {/* Backdrop */}
            {open && (
                <div
                    className="fixed inset-0 z-[60] transition-opacity"
                    style={{ background: 'rgba(0,0,0,0.2)', backdropFilter: 'blur(4px)' }}
                    onClick={onClose}
                />
            )}

            {/* Drawer panel — orientation-aware width via CSS class */}
            <div
                className="qio-chat-drawer fixed top-0 right-0 z-[70] h-full flex flex-col transition-transform duration-300 ease-out"
                style={{
                    transform: open ? 'translateX(0)' : 'translateX(100%)',
                    background: '#ffffff',
                    borderLeft: '1px solid #e5e7eb',
                    boxShadow: open ? '-8px 0 30px rgba(0,0,0,0.08)' : 'none',
                    overflow: 'hidden',
                }}
            >
                {/* Header */}
                <div
                    className="flex items-center justify-between px-5 py-4"
                    style={{ borderBottom: '1px solid #f0f0f0' }}
                >
                    <div className="flex items-center gap-3">
                        <div
                            className="w-9 h-9 rounded-xl overflow-hidden"
                            style={{ border: '1px solid #d0eded' }}
                        >
                            {/* eslint-disable-next-line @next/next/no-img-element */}
                            <img
                                src="/branding/qio-logo.jpeg"
                                alt="Qio"
                                width={36}
                                height={36}
                                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            />
                        </div>
                        <div>
                            <div className="text-sm font-bold" style={{ color: '#1a1a2e' }}>اسأل كيو</div>
                            <div className="text-[10px]" style={{ color: '#94a3b8' }}>AI Powered by QIO</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {creditStatus && (
                            <div
                                className="flex flex-col items-end leading-tight"
                                style={{ fontSize: 10 }}
                                title={`رصيدك اليومي — يتجدد خلال ${formatCountdown(displaySecondsRemaining)}`}
                            >
                                <span
                                    className="font-bold"
                                    style={{
                                        color: creditStatus.remainingCredits > 0 ? '#0d8f8f' : '#b45309',
                                        direction: 'ltr',
                                    }}
                                >
                                    {creditStatus.remainingCredits} / {creditStatus.dailyCredits}
                                </span>
                                <span style={{ color: '#94a3b8' }} dir="rtl">
                                    يتجدد بعد{' '}
                                    <span style={{ direction: 'ltr', display: 'inline-block', fontVariantNumeric: 'tabular-nums' }}>
                                        {formatCountdown(displaySecondsRemaining)}
                                    </span>
                                </span>
                            </div>
                        )}
                        <button
                            onClick={onClose}
                            className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors hover:bg-slate-100"
                            style={{ color: '#94a3b8' }}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Messages area — dynamic padding when keyboard visible */}
                <div
                    ref={scrollRef}
                    className="flex-1 overflow-y-auto px-4 py-4 space-y-4"
                    dir="rtl"
                    style={{ paddingBottom: showOSK ? `${oskHeight + 8}px` : undefined }}
                >
                    {(messages ?? []).map((msg, i) => {
                        // Only the hero product card breaks out to the full chat width;
                        // text bubbles, chips, budget and legacy cards stay at max-w-[85%].
                        // When a hero is present, the outer wrapper becomes a full-width
                        // flex column and each non-hero section gets self-start max-w-[85%].
                        const hasHero = msg.role === 'ai' && !!msg.blocks?.some(
                            b => b.type === 'product_card' && b.product.role === 'hero'
                        );
                        const wrapperClass = hasHero
                            ? 'w-full flex flex-col space-y-2'
                            : 'max-w-[85%] space-y-2';
                        const constrain85 = hasHero ? 'self-start max-w-[85%]' : undefined;
                        // The first AI bubble carries the premium onboarding intro.
                        // The rotating hint renders tightly beneath it and vanishes
                        // on first interaction — no page-hero, just a sibling line.
                        const isFirstAiBubble = msg.role === 'ai'
                            && (messages ?? []).findIndex(m => m.role === 'ai') === i;
                        const showHintUnder = isFirstAiBubble && hintsActive;

                        // ── Block walker (hoisted) — shared by render + copy ──
                        // Pre-compute the per-AI-message block entries at this
                        // scope so both the inline block render (IIFE below)
                        // and the action-bar Copy button (further below) work
                        // from the SAME structure. Runs only for AI messages
                        // that carry blocks; otherwise both arrays stay empty
                        // and every downstream check short-circuits.
                        type RenderEntry =
                            | { kind: 'group'; titleText?: string; narrative?: string; trustLine?: string; card: ProductSuggestion; isHero: boolean }
                            | { kind: 'bridge'; text: string }
                            | { kind: 'section'; text: string };
                        const blockEntries: RenderEntry[] = [];
                        // Routine-only inline bubbles — singletons at the opener/total
                        // positions. Absent (undefined) for every non-routine message.
                        let routineOpenerText: string | undefined;
                        let routineTotalText: string | undefined;
                        // Non-routine leading opener: any text block(s) that sit BEFORE
                        // the first product_* / routine_* block get collected here and
                        // rendered above the group sequence. This is what rescues the
                        // LLM's acknowledgement sentence on hero+budget / alternatives
                        // responses (previously dropped by the walker filter).
                        let topOpenerText: string | undefined;
                        if (msg.role === 'ai' && msg.blocks && msg.blocks.length > 0) {
                            const leadingTexts: string[] = [];
                            let seenProductBlock = false;
                            const displayBlocks = msg.blocks.filter(b => {
                                if (b.type === 'product_title' || b.type === 'product_card' || b.type === 'bridge_text' || b.type === 'trust_line' || b.type === 'section_title') {
                                    seenProductBlock = true;
                                    return true;
                                }
                                if (b.type === 'routine_opener' || b.type === 'routine_total') {
                                    return true;
                                }
                                if (b.type === 'text') {
                                    if (seenProductBlock) return true;   // per-product narrative
                                    // Leading text: capture as top opener, exclude from walker.
                                    leadingTexts.push(b.text);
                                    return false;
                                }
                                return false;
                            });
                            if (leadingTexts.length > 0) {
                                topOpenerText = leadingTexts.join('\n\n').trim();
                            }
                            let pendingTitle: string | undefined;
                            let pendingNarrative: string | undefined;
                            let pendingTrustLine: string | undefined;
                            for (const b of displayBlocks) {
                                if (b.type === 'routine_opener') {
                                    routineOpenerText = b.text;
                                } else if (b.type === 'routine_total') {
                                    routineTotalText = b.text;
                                } else if (b.type === 'product_title') {
                                    pendingTitle = b.text;
                                } else if (b.type === 'text') {
                                    pendingNarrative = b.text;
                                } else if (b.type === 'trust_line') {
                                    // Separate slot — never overwrites pendingNarrative.
                                    pendingTrustLine = b.text;
                                } else if (b.type === 'product_card') {
                                    blockEntries.push({
                                        kind: 'group',
                                        titleText: pendingTitle,
                                        narrative: pendingNarrative,
                                        trustLine: pendingTrustLine,
                                        card: b.product,
                                        isHero: b.product.role === 'hero',
                                    });
                                    pendingTitle = undefined;
                                    pendingNarrative = undefined;
                                    pendingTrustLine = undefined;
                                } else if (b.type === 'bridge_text') {
                                    blockEntries.push({ kind: 'bridge', text: b.text });
                                } else if (b.type === 'section_title') {
                                    blockEntries.push({ kind: 'section', text: b.text });
                                }
                            }
                        }
                        // Used by the render to suppress the top combined bubble
                        // AND by the copy path to decide whether to reconstruct.
                        const hasPerProductNarrative = blockEntries.some(
                            e => e.kind === 'group' && !!e.narrative && e.narrative.trim().length > 0,
                        );

                        // ── Copy payload — mirrors the ON-SCREEN content ──
                        // When per-product narratives exist the top combined
                        // bubble (`msg.text`) is hidden by the render, and in
                        // some cases msg.text carries the validated/fallback
                        // template from validation.service.ts (e.g. "عندنا
                        // منتجات حلوة …") which never actually appeared on
                        // screen. Rebuild the copy text from blockEntries[] in
                        // the SAME order the UI walks them, with the same
                        // stripMarkdown normalization. For any other AI path
                        // (text-only, legacy cards, graceful fallback without
                        // narratives) and for user bubbles, fall back to
                        // msg.text — current behavior preserved byte-for-byte.
                        const reconstructedCopy = hasPerProductNarrative
                            ? blockEntries.map(e => {
                                if (e.kind === 'bridge') return stripMarkdown(e.text).trim();
                                if (e.kind === 'section') return stripMarkdown(e.text).trim();
                                const lines: string[] = [];
                                const headRaw = (e.titleText && e.titleText.trim()) || e.card.nameAr;
                                const head = headRaw ? headRaw.trim() : '';
                                if (head) lines.push(head);
                                if (e.narrative && e.narrative.trim()) {
                                    lines.push(stripMarkdown(e.narrative).trim());
                                }
                                // Meta line (brand + price). When the head is
                                // already the product name (common hero/budget
                                // case where titleText is nameAr), we SKIP the
                                // name in the meta line to avoid repeating it
                                // right under the head. When the head is a
                                // different label (legacy product_title group
                                // where nameAr lives only in the card), we
                                // include the full `name | brand | price` so
                                // the paste still carries the product id.
                                const brandAr = e.card.brandAr?.trim() || '';
                                const hasPrice = typeof e.card.price === 'number' && e.card.price > 0;
                                const priceStr = hasPrice ? `${e.card.price} ريال` : '';
                                const nameNormalized = e.card.nameAr ? e.card.nameAr.trim() : '';
                                const headIsName = !!nameNormalized && head === nameNormalized;
                                const metaParts = headIsName
                                    ? [brandAr, priceStr].filter(s => s.length > 0)
                                    : [nameNormalized, brandAr, priceStr].filter(s => s.length > 0);
                                if (metaParts.length > 0) {
                                    lines.push(metaParts.join(' | '));
                                }
                                return lines.join('\n');
                            }).filter(s => s && s.length > 0).join('\n\n').trim()
                            : '';
                        const copyPayload = reconstructedCopy || msg.text;

                        return (
                        <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                            <div className={wrapperClass}>
                                {/*
                                 * ══════════════════════════════════════════════════════
                                 * UI CONTRACT — PERMANENT PRODUCT RENDERING RULE
                                 * ══════════════════════════════════════════════════════
                                 * For every product recommendation, the UI MUST render:
                                 *
                                 *   Product Name  (teal text label)
                                 *   [Product Card] (clickable card with image)
                                 *
                                 * This repeats for 1, 3, 5, 10, or any N products.
                                 *
                                 * RULES:
                                 * - Product rendering is CODE-DRIVEN, never LLM-driven.
                                 * - The LLM controls ONLY the intro text bubble.
                                 * - Do NOT merge product name into the card.
                                 * - Do NOT remove the product name text above the card.
                                 * - Do NOT render products as text-only lists.
                                 * - This contract is PERMANENT regardless of prompt or
                                 *   model changes.
                                 * ══════════════════════════════════════════════════════
                                 */}
                                {(() => {
                                    // User messages: plain bubble
                                    if (msg.role === 'user') {
                                        return (
                                            <div
                                                className="px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-line"
                                                style={{ background: '#0ea5a5', color: '#ffffff', borderBottomRightRadius: '4px' }}
                                            >
                                                {(msg.imageUrl || msg.imageThumbUrl) && (
                                                    /* QCARE-IMAGE-UPLOAD-THUMBNAIL-PERSISTENCE-HOTFIX:
                                                       prefer the live full-size data URL when present
                                                       (current session, sharper). Fall back to the
                                                       persisted thumbnail (imageThumbUrl) so refreshed
                                                       sessions still see a preview instead of a blank bubble. */
                                                    /* eslint-disable-next-line @next/next/no-img-element */
                                                    <img src={msg.imageUrl || msg.imageThumbUrl} alt="User photo" className="w-48 h-auto rounded-lg mb-2 object-cover border border-[#0d8f8f]" />
                                                )}
                                                {msg.text !== 'إليك هذه الصورة' ? msg.text : ''}
                                            </div>
                                        );
                                    }

                                    // AI with blocks: the block walker + hasPerProductNarrative
                                    // were computed one scope up (messages.map) so the Copy
                                    // button in the action-bar can read the same structure. We
                                    // consume blockEntries / hasPerProductNarrative directly
                                    // here instead of re-walking.
                                    if (msg.blocks && msg.blocks.length > 0) {
                                        const entries = blockEntries;
                                        return (
                                            <>
                                                {/* Routine opener — inline bubble that owns the header
                                                    slot when this is a routine response. Suppresses
                                                    the combined msg.text top bubble below (the routine
                                                    text is already decomposed into opener + per-step
                                                    bubbles + total). */}
                                                {routineOpenerText && routineOpenerText.trim() && (
                                                    <div className={constrain85}>
                                                        <div
                                                            className="px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-line"
                                                            style={{ background: '#f8f9fb', color: '#334155', border: '1px solid #f0f0f0', userSelect: 'text', WebkitUserSelect: 'text' }}
                                                        >
                                                            {renderAssistantText(routineOpenerText)}
                                                        </div>
                                                    </div>
                                                )}
                                                {/* Non-routine top opener — rescued from the leading
                                                    text block(s) that sit BEFORE the first product
                                                    block. Renders above the hero/budget group sequence
                                                    so the LLM's acknowledgement is visible again. */}
                                                {!routineOpenerText && topOpenerText && topOpenerText.trim() && (
                                                    <div className={constrain85}>
                                                        <div
                                                            className="px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-line"
                                                            style={{ background: '#f8f9fb', color: '#334155', border: '1px solid #f0f0f0', userSelect: 'text', WebkitUserSelect: 'text' }}
                                                        >
                                                            {renderAssistantText(topOpenerText)}
                                                        </div>
                                                    </div>
                                                )}
                                                {/* AI conversational text — shown only when the backend
                                                    did NOT emit per-product narrative blocks, so the
                                                    combined opener doesn't duplicate the per-product
                                                    bubbles below. Also suppressed whenever a routine
                                                    opener or a top opener is present — those bubbles
                                                    already own the header slot. */}
                                                {msg.text && msg.text.trim() && !hasPerProductNarrative && !routineOpenerText && !topOpenerText && (
                                                    <div className={constrain85}>
                                                        <div
                                                            className="px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-line"
                                                            style={{ background: '#f8f9fb', color: '#334155', border: '1px solid #f0f0f0', userSelect: 'text', WebkitUserSelect: 'text' }}
                                                        >
                                                            {renderAssistantText(msg.text)}
                                                        </div>
                                                    </div>
                                                )}
                                                {/* Mixed render — hero breaks out to full width; others (budget,
                                                    legacy, bridge_text) stay at max-w-[85%] via constrain85. */}
                                                {entries.map((e, ei) => {
                                                    if (e.kind === 'bridge') {
                                                        // QCARE-AI-DEV-PHASE-R5N (2026-05-07) — connector text
                                                        // typography aligned with the assistant prose bubble
                                                        // ("text-sm leading-relaxed" + slate body color)
                                                        // so inter-card text reads as continuous chat copy
                                                        // instead of a small muted caption.
                                                        return (
                                                            <div
                                                                key={`br-${ei}`}
                                                                className={`my-3 text-sm leading-relaxed${constrain85 ? ` ${constrain85}` : ''}`}
                                                                style={{ color: '#334155', direction: 'rtl', paddingRight: '4px' }}
                                                            >
                                                                {e.text}
                                                            </div>
                                                        );
                                                    }
                                                    if (e.kind === 'section') {
                                                        return (
                                                            <div
                                                                key={`sec-${ei}`}
                                                                className={constrain85}
                                                            >
                                                                <div
                                                                    className="mt-4 mb-2 px-4 py-2 rounded-2xl text-sm font-bold"
                                                                    style={{ background: '#ecfeff', color: '#0f766e', border: '1px solid #a5f3fc', direction: 'rtl' }}
                                                                >
                                                                    {e.text}
                                                                </div>
                                                            </div>
                                                        );
                                                    }
                                                    return (
                                                        <div
                                                            key={`g-${ei}`}
                                                            className={e.isHero ? 'w-full' : constrain85}
                                                        >
                                                            {e.titleText && !e.isHero && e.card.role !== 'budget' && (
                                                                <div
                                                                    className="px-3 py-1.5 text-xs font-bold"
                                                                    style={{ color: '#0d8f8f', marginBottom: '-4px' }}
                                                                >
                                                                    {e.titleText}
                                                                </div>
                                                            )}
                                                            {/* Per-product narrative bubble — styled like the top
                                                                combined bubble so it reads as an AI-authored
                                                                introduction. Rendered at hero full-width OR at
                                                                constrain85 for budget/legacy, matching the card
                                                                width beneath it. */}
                                                            {e.narrative && e.narrative.trim() && (
                                                                <div className={e.isHero ? 'self-start max-w-[85%]' : undefined}>
                                                                    <div
                                                                        className="px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-line mb-2"
                                                                        style={{ background: '#f8f9fb', color: '#334155', border: '1px solid #f0f0f0', userSelect: 'text', WebkitUserSelect: 'text' }}
                                                                    >
                                                                        {renderAssistantText(e.narrative)}
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {/* Trust line — authenticity annotation attached to
                                                                the group (hero today). Separate slot from the
                                                                narrative so the LLM pitch above stays intact. */}
                                                            {e.trustLine && e.trustLine.trim() && (
                                                                <div className={e.isHero ? 'self-start max-w-[85%]' : undefined}>
                                                                    <div
                                                                        className="px-3 py-2 rounded-xl text-xs leading-relaxed mb-2"
                                                                        style={{ background: '#ecfeff', color: '#0f766e', border: '1px solid #a5f3fc', userSelect: 'text', WebkitUserSelect: 'text' }}
                                                                    >
                                                                        {renderAssistantText(e.trustLine)}
                                                                    </div>
                                                                </div>
                                                            )}
                                                            <CardRenderer
                                                                product={mergeProductImage(e.card, latestImageMap)}
                                                                layout={msg.meta?.layout}
                                                            />
                                                        </div>
                                                    );
                                                })}
                                                {/* Routine total — compact trailing bubble with the sum.
                                                    Routine-only by construction; absent for every other
                                                    message type. */}
                                                {routineTotalText && routineTotalText.trim() && (
                                                    <div className={constrain85}>
                                                        <div
                                                            className="px-4 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-line"
                                                            style={{ background: '#ecfeff', color: '#0f766e', border: '1px solid #a5f3fc', fontWeight: 600, userSelect: 'text', WebkitUserSelect: 'text' }}
                                                        >
                                                            {renderAssistantText(routineTotalText)}
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        );
                                    }

                                    // Legacy fallback (no blocks field) — still enforces the contract:
                                    // product_title above product_card for every product.
                                    const imageProducts = (msg.productSuggestions || []);

                                    if (imageProducts.length === 0) {
                                        return (
                                            <div
                                                className="px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-line"
                                                style={{ background: '#f8f9fb', color: '#334155', border: '1px solid #f0f0f0', userSelect: 'text', WebkitUserSelect: 'text' }}
                                            >
                                                {renderAssistantText(msg.text)}
                                            </div>
                                        );
                                    }

                                    // Legacy path — same contract: product_title + product_card per product
                                    return (
                                        <>
                                            <div
                                                className="px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-line"
                                                style={{ background: '#f8f9fb', color: '#334155', border: '1px solid #f0f0f0', userSelect: 'text', WebkitUserSelect: 'text' }}
                                            >
                                                {renderAssistantText(msg.text)}
                                            </div>
                                            {imageProducts.map((ps) => (
                                                <div key={ps.productId} className="space-y-0.5">
                                                    {/* UI CONTRACT: product_title above product_card — always */}
                                                    <div
                                                        className="px-3 py-1.5 text-xs font-bold"
                                                        style={{ color: '#0d8f8f', marginBottom: '-4px' }}
                                                    >
                                                        {ps.nameAr}
                                                    </div>
                                                    <CardRenderer product={mergeProductImage(ps, latestImageMap)} layout={msg.meta?.layout} />
                                                </div>
                                            ))}
                                        </>
                                    );
                                })()}
                                {/* Message actions — [⧉] [👍] [👎] [⋯] — AI bubbles only.
                                    Shared with mobile via components/chat/MessageActions. */}
                                {msg.role === 'ai' && !!msg.text && msg.text.trim().length > 0 && (
                                    <MessageActions
                                        actions={messageActions}
                                        messageIndex={i}
                                        messageId={msg.id}
                                        messageText={msg.text}
                                        copyPayload={copyPayload}
                                        className={constrain85 || undefined}
                                    />
                                )}
                                {/* Suggestion chips — below blocks.
                                    QCARE-CHIP-VISIBILITY-POLICY (2026-05-12):
                                    apply the same backend route-aware policy
                                    at render time. This is the universal
                                    safety net for (a) historical chips
                                    persisted in `uiPayload.suggestions[]`
                                    before the backend rollout, and (b) any
                                    backend emit site that wasn't wrapped
                                    explicitly. Stack traces / raw JSON never
                                    surface — only the final clamped chip
                                    list. See `apps/kiosk-web/lib/chip-
                                    policy.ts` for the rules; mirrors
                                    `applyChipPolicy` in `apps/backend/src/
                                    modules/ai-chat/smart-chips.ts`. */}
                                {msg.role === 'ai' && msg.suggestions && msg.suggestions.length > 0 && (() => {
                                    const visibleChips = applyChipPolicy(msg.suggestions, {
                                        composedRoute: msg.meta?.composedRoute ?? null,
                                        layout: msg.meta?.layout ?? null,
                                        productsCount: msg.productSuggestions?.length ?? 0,
                                    });
                                    if (visibleChips.length === 0) return null;
                                    return (
                                    <div className={`flex flex-wrap gap-2${hasHero ? ' self-start max-w-[85%]' : ''}`}>
                                        {visibleChips.map((s, j) => (
                                            <button
                                                key={j}
                                                onClick={() => {
                                                    // Phase 9R — trust chips open TrustModal in
                                                    // place; everything else goes through the
                                                    // normal send path.
                                                    if (isTrustChipText(s)) {
                                                        setTrustModalOpen(true);
                                                        return;
                                                    }
                                                    handleSend(s);
                                                }}
                                                className="px-3 py-1.5 rounded-full text-xs font-medium transition-all hover:bg-teal-50"
                                                style={{
                                                    background: '#f0fafa',
                                                    border: '1px solid #d0eded',
                                                    color: '#0d8f8f',
                                                }}
                                            >
                                                {s}
                                            </button>
                                        ))}
                                    </div>
                                    );
                                })()}
                                {showHintUnder && (
                                    <div
                                        key={`hint-${hintIndex}`}
                                        aria-live="polite"
                                        style={{
                                            fontSize: 12.5,
                                            fontWeight: 500,
                                            color: '#2d9595',
                                            lineHeight: 1.55,
                                            minHeight: 19,
                                            direction: 'rtl',
                                            textAlign: 'right',
                                            paddingInline: 8,
                                            marginTop: 4,
                                            animation: 'q-hint-fade 2800ms ease-in-out forwards',
                                        }}
                                    >
                                        {ONBOARDING_HINTS[hintIndex]}
                                    </div>
                                )}
                            </div>
                        </div>
                        );
                    })}

                    {/* Typing indicator */}
                    {isTyping && (
                        <div className="flex justify-start">
                            <div
                                className="px-4 py-3 rounded-2xl"
                                style={{ background: '#f8f9fb', border: '1px solid #f0f0f0' }}
                            >
                                <div className="dot-indicator">
                                    <span /><span /><span />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Input area */}
                <form
                    onSubmit={handleSubmit}
                    className="flex flex-col"
                    style={{ borderTop: '1px solid #f0f0f0' }}
                    dir="rtl"
                >
                    {/* Image Preview Area */}
                    {imageBase64 && (
                        <div className="px-4 pt-3 flex items-center gap-2">
                            <div className="relative">
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img src={imageBase64} alt="Preview" className="w-16 h-16 object-cover rounded-lg border border-[#e5e7eb]" />
                                <button
                                    type="button"
                                    onClick={() => { setImageBase64(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                                    className="absolute -top-2 -right-2 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-md text-xs border border-[#e5e7eb]"
                                    aria-label="إزالة الصورة"
                                >
                                    ✕
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="px-4 py-3 flex gap-2">
                        <input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            ref={fileInputRef}
                            onChange={handleImageSelect}
                            className="hidden"
                        />
                        <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); fileInputRef.current?.click(); }}
                            className="px-3 py-3 rounded-xl transition-colors flex items-center justify-center shrink-0"
                            style={{
                                background: '#f8f9fb',
                                border: '1px solid #e5e7eb',
                                color: '#1a1a2e',
                            }}
                            title="إرفاق صورة"
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
                                <circle cx="12" cy="13" r="4"></circle>
                            </svg>
                        </button>
                        <input
                            ref={inputRef}
                            type="text"
                            value={input}
                            onChange={e => {
                                setInput(e.target.value);
                                // Only stop hints once the drawer auto-focus window
                                // has passed. Programmatic focus dispatches trusted
                                // events, so isTrusted is NOT a safe discriminator.
                                if (hintsArmedRef.current && hintsActive) setHintsActive(false);
                            }}
                            onFocus={() => {
                                if (hintsArmedRef.current && hintsActive) setHintsActive(false);
                            }}
                            onClick={forceFocus}
                        placeholder="اكتب سؤالك هنا..."
                        maxLength={500}
                        autoComplete="off"
                        autoCorrect="off"
                        enterKeyHint="send"
                        className="flex-1 px-4 py-3 rounded-xl text-sm transition-colors focus:outline-none"
                        style={{
                            background: '#f8f9fb',
                            border: '1px solid #e5e7eb',
                            color: '#1a1a2e',
                            caretColor: '#0ea5a5',
                        }}
                    />
                    <button
                        type="button"
                        onPointerDown={(e) => { e.preventDefault(); setShowOSK(v => !v); forceFocus(); }}
                        onClick={(e) => { e.preventDefault(); }}
                        className="px-3 py-3 rounded-xl text-xs transition-colors"
                        style={{
                            background: showOSK ? '#d0eded' : '#f0fafa',
                            border: '1px solid #d0eded',
                            color: '#0d8f8f',
                        }}
                        aria-label="فتح لوحة المفاتيح"
                        title="فتح الكيبورد"
                    >
                        ⌨️
                    </button>
                    <button
                        type="submit"
                        disabled={(!input.trim() && !imageBase64) || isTyping}
                        className="px-4 py-3 rounded-xl font-medium text-sm transition-all disabled:opacity-30 flex items-center justify-center shrink-0"
                        style={{ background: '#0ea5a5', color: '#ffffff' }}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: 'rotate(180deg)' }}>
                            <line x1="22" y1="2" x2="11" y2="13" />
                            <polygon points="22 2 15 22 11 13 2 9 22 2" />
                        </svg>
                    </button>
                    </div>
                </form>

                {/* In-app Arabic keyboard */}
                <OnScreenKeyboard
                    visible={showOSK}
                    onKey={(ch) => setInput(prev => prev + ch)}
                    onBackspace={() => setInput(prev => prev.slice(0, -1))}
                    onSpace={() => setInput(prev => prev + ' ')}
                    onSend={() => { handleSend(); setShowOSK(false); }}
                    onClose={() => setShowOSK(false)}
                    onHeightChange={handleOSKHeight}
                />

                {/* Dev-only focus debug */}
                {process.env.NODE_ENV !== 'production' && (
                    <div style={{ fontSize: '10px', color: '#94a3b8', textAlign: 'center', padding: '2px' }}>
                        focus: {typeof document !== 'undefined' && document.activeElement === inputRef.current ? 'ok' : 'no'}
                    </div>
                )}
            </div>
        </>
    );
}

// ──────────────────────────────────────────────────────
// Client-only export.
//
// ChatDrawerInner reads localStorage inside its useState lazy initializers
// (customerId, transcript restore). During SSR `typeof window` is undefined,
// the initializers return empty state, and React hydrates with that empty
// state — useState lazy initializers do NOT re-run on the client after
// SSR, so the transcript would never load.
//
// Wrapping with `dynamic(..., { ssr: false })` defers the mount to the
// client, where localStorage is available at lazy-init time. Mirrors the
// mobile /m/q page's pattern (app/m/q/page.tsx:981-1017). Consumer API is
// unchanged — `import { ChatDrawer } from '@/components/ChatDrawer'` still
// resolves to the same JSX contract.
// ──────────────────────────────────────────────────────
export const ChatDrawer = dynamic(
    () => Promise.resolve(ChatDrawerInner),
    { ssr: false },
);
